//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS } from '@account-settings/redux/facebook-instagram/facebookInstagramSettingsConstants';
import {
  ValidFBOAuthRedirectPath,
  generateFacebookOAuthUrl,
} from '@account-settings/redux/facebook-instagram/facebookInstagramSettingsUtils';
import { getIsOmitMetaMessagingScopesEnabled } from '@account-settings/redux/facebook-instagram/selectors/getIsOmitMetaMessagingScopesEnabled';
import { getHasMultiHandleInstagramEnabled } from '@account-settings/redux/facebook-instagram/selectors/getMultiHandleFeature';
import * as gatewayFacebookSettingsApi from '@apis/facebook-settings/gatewayFacebookSettingsApi';
import * as gatewayInstagramSettingsApi from '@apis/instagram-settings/gatewayInstagramSettingsApi';
import {
  IConnectedBrandInstagramBusinessAccount,
  IConnectedBrandInstagramBusinessAccounts,
} from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import * as instagramSettingsApi from '@apis/instagram-settings/instagramSettingsApi';
import { facebookPageActions } from '@facebook/actions/facebookPageActions';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import IFacebookPageToken from '@models/IFacebookPageToken';
import IFacebookUserToken from '@models/IFacebookUserToken';
import { createThunk } from '@redux/createThunk';
import { createAction } from '@utils/actions';
import { FetchActionCreators, makeFetchActions } from '@utils/makeFetchActions';

import { ReduxDispatch, ReduxGetState } from 'app/curalate.store';
import gaApi from 'app/initialization/ga/ga-api';

const handleOAuth = () =>
  createAction({
    type: FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.START_OAUTH,
  });

const getFacebookUserToken = makeFetchActions(
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.REQUEST_FACEBOOK_USER_TOKEN,
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_FACEBOOK_USER_TOKEN_SUCCEEDED,
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_FACEBOOK_USER_TOKEN_FAILED,
  (facebookUserToken?: IFacebookUserToken) => ({ facebookUserToken }),
);

const getConnectedInstagramAccount = makeFetchActions(
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.REQUEST_CONNECTED_IG_ACCOUNT,
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_IG_ACCOUNT_SUCCEEDED,
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_IG_ACCOUNT_FAILED,
  (connectedInstagramAccount?: IConnectedBrandInstagramBusinessAccount) => ({
    connectedInstagramAccount,
  }),
);

const getConnectedMultiHandleInstagramAccount = makeFetchActions(
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.REQUEST_CONNECTED_IG_ACCOUNT_MULTIHANDLE,
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_IG_ACCOUNT_SUCCEEDED_MULTIHANDLE,
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_IG_ACCOUNT_FAILED_MULTIHANDLE,
  (
    connectedInstagramAccounts?: IConnectedBrandInstagramBusinessAccounts[],
  ) => ({
    connectedInstagramAccounts,
  }),
);

const getConnectedFacebookPageToken = makeFetchActions(
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.REQUEST_CONNECTED_FB_PAGE,
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_FB_PAGE_SUCCEEDED,
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_CONNECTED_FB_PAGE_FAILED,
  (connectedFacebookPageToken?: IFacebookPageToken) => ({
    connectedFacebookPageToken,
  }),
);

const handleDeauth = makeFetchActions(
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.REQUEST_DEAUTH_FB,
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_DEAUTH_FB_SUCCEEDED,
  FACEBOOK_INSTAGRAM_SETTINGS_ACTIONS.RECEIVE_DEAUTH_FB_FAILED,
);

export type FacebookInstagramSettingsActions = ReturnType<
  | typeof handleOAuth
  | FetchActionCreators<typeof getFacebookUserToken>
  | FetchActionCreators<typeof getConnectedInstagramAccount>
  | FetchActionCreators<typeof getConnectedMultiHandleInstagramAccount>
  | FetchActionCreators<typeof getConnectedFacebookPageToken>
  | FetchActionCreators<typeof handleDeauth>
>;

const fetchConnectedInstagramAccount =
  () => async (dispatch: ReduxDispatch) => {
    dispatch(getConnectedInstagramAccount.requested());

    try {
      const {
        data: { connectedAccount },
      } = await gatewayInstagramSettingsApi.getConnectedInstagramAccount();
      dispatch(getConnectedInstagramAccount.succeeded(connectedAccount));
      return connectedAccount;
    } catch (_e) {
      dispatch(getConnectedInstagramAccount.failed(''));
    }
  };

const fetchConnectedMultiHanldeInstagramAccount =
  () => async (dispatch: ReduxDispatch) => {
    dispatch(getConnectedMultiHandleInstagramAccount.requested());

    try {
      const {
        data: { connectedAccounts },
      } =
        await gatewayInstagramSettingsApi.getConnectedMultiHandleInstagramAccount();
      dispatch(
        getConnectedMultiHandleInstagramAccount.succeeded(connectedAccounts),
      );
      return connectedAccounts;
    } catch (_e) {
      dispatch(getConnectedMultiHandleInstagramAccount.failed(''));
    }
  };

const fetchConnectedFacebookPage =
  () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
    const { isFetchingConnectedFacebookPageToken } =
      getState().facebookInstagramSettings.flags;

    if (isFetchingConnectedFacebookPageToken) {
      return;
    }

    dispatch(getConnectedFacebookPageToken.requested());

    try {
      const {
        data: { facebookPageToken },
      } = await gatewayFacebookSettingsApi.getConnectedFacebookPageToken();
      dispatch(getConnectedFacebookPageToken.succeeded(facebookPageToken));
    } catch (_e) {
      dispatch(getConnectedFacebookPageToken.failed(''));
    }
  };

export const facebookInstagramSettingsActions = {
  fetchFacebookUserToken:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const { isFetchingFacebookUserToken } =
        getState().facebookInstagramSettings.flags;

      if (isFetchingFacebookUserToken) {
        return;
      }

      dispatch(getFacebookUserToken.requested());

      try {
        const { data } =
          await gatewayFacebookSettingsApi.getFacebookUserToken();
        dispatch(getFacebookUserToken.succeeded(data.facebookUserToken));
      } catch (_e) {
        dispatch(
          getFacebookUserToken.failed(
            'There was an issue retrieving your Facebook connection details.',
          ),
        );
      }
    },

  /**
   * Checks if the FB & IG tokens are loaded, and dispatches calls to load them if they are not.
   */
  loadConnections: createThunk(() => async (dispatch, getState) => {
    const { connectedFacebookPageToken, connectedInstagramAccount } =
      getState().facebookInstagramSettings;
    const isMutliHandleFeatureEnabled =
      getHasMultiHandleInstagramEnabled(getState());
    // Load connected IG account, if we haven't already.
    const igPromise = async () => {
      if (!connectedInstagramAccount) {
        if (!isMutliHandleFeatureEnabled) {
          return dispatch(fetchConnectedInstagramAccount());
        } else {
          return dispatch(fetchConnectedMultiHanldeInstagramAccount());
        }
      }
    };

    // Load connected FB account, if we haven't already.
    const fbPromise = async () => {
      if (!connectedFacebookPageToken) {
        return dispatch(facebookPageActions.fetchPageTokenAndMetadata());
      }
    };

    return Promise.all([igPromise(), fbPromise()]);
  }),

  fetchConnectedInstagramAccount,
  fetchConnectedMultiHanldeInstagramAccount,
  fetchConnectedFacebookPage,

  triggerOAuth: createThunk(
    (isRerequest: boolean = false, redirectPath?: ValidFBOAuthRedirectPath) =>
      async (dispatch, getState) => {
        dispatch(handleOAuth());

        const shouldOmitMetaMessagingScopes =
          getIsOmitMetaMessagingScopesEnabled(getState());

        const endpoint = await generateFacebookOAuthUrl(
          isRerequest,
          redirectPath,
          shouldOmitMetaMessagingScopes,
        );

        location.assign(endpoint);
      },
  ),

  triggerDeauth: () => async (dispatch: ReduxDispatch) => {
    dispatch(handleDeauth.requested());

    try {
      await gatewayFacebookSettingsApi.deauthFacebook();
      dispatch(handleDeauth.succeeded());

      gaApi.recordEvent(
        gaApi.CATEGORY_SETTINGS,
        gaApi.ACTION_FACEBOOK_DISCONNECT_ACCOUNT,
        gaApi.LABEL_SUCCESS,
      );

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Facebook account successfully disconnected!',
          type: ToastType.SUCCESS,
        }),
      );
    } catch (_e) {
      dispatch(handleDeauth.failed(''));

      gaApi.recordEvent(
        gaApi.CATEGORY_SETTINGS,
        gaApi.ACTION_FACEBOOK_DISCONNECT_ACCOUNT,
        gaApi.LABEL_FAIL,
      );

      dispatch(
        toastNotificationsActions.addToastNotification({
          title:
            'An error occurred when trying to disconnect your Facebook account.',
          type: ToastType.ERROR,
        }),
      );

      return;
    }

    try {
      await instagramSettingsApi.disconnectLike2Buy();
    } catch {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title:
            'An error occurred when trying to disconnect your Like2Buy store.',
          type: ToastType.ERROR,
        }),
      );
    }
  },
};
